import React from "react";
import { motion } from "framer-motion";
import { ShieldCheck, Boxes, Cable, Award } from "lucide-react";
import { Reveal, SectionLabel } from "./Shared";
import { STATS } from "../../data/content";

const SNAPSHOT = [
  { icon: Boxes, text: "Professionally managed telecom implementation organization" },
  { icon: Cable, text: "Expertise in Optical Access Networks, OLT Deployment & Infrastructure Projects" },
  { icon: Award, text: "Active execution partner for leading telecom operators" },
  { icon: ShieldCheck, text: "Strong focus on quality, safety and compliance standards" },
];

export default function About() {
  return (
    <section id="about" className="relative py-24 md:py-32 border-t border-white/5" data-testid="about-section">
      <div className="max-w-[1400px] mx-auto px-5 md:px-10">
        <div className="grid md:grid-cols-12 gap-10 md:gap-16 items-start">
          <div className="md:col-span-7">
            <SectionLabel num="[ 01 ]">About Us</SectionLabel>
            <Reveal>
              <h2 className="font-display font-extrabold text-white tracking-tighter leading-[0.95] text-4xl md:text-6xl">
                A trusted execution partner in telecom.
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-8 max-w-2xl text-base md:text-lg leading-relaxed" style={{ color: "#94A3B8" }}>
                A3NETWORKS (OPC) Private Limited is a professionally managed
                telecom implementation and material supply company specializing
                in Optical Access Networks, Broadband Infrastructure, Wireless
                Backhaul, and Telecom Network Deployment Projects.
              </p>
            </Reveal>
            <Reveal delay={0.15}>
              <p className="mt-5 max-w-2xl text-base leading-relaxed" style={{ color: "#64748B" }}>
                Established in August 2025, the company operates as a trusted
                execution partner for telecom operators, OEMs, ISPs, enterprises
                and system integrators.
              </p>
            </Reveal>

            <div className="mt-12 grid sm:grid-cols-2 gap-px bg-white/5 border border-white/5">
              {SNAPSHOT.map((s, i) => {
                const Icon = s.icon;
                return (
                  <Reveal key={i} delay={i * 0.08}>
                    <div className="card-hover bg-[#0C1322] p-6 h-full flex gap-4" data-testid={`snapshot-${i}`}>
                      <Icon size={22} className="text-cyan shrink-0 mt-1" style={{ color: "#00F0FF" }} />
                      <p className="text-sm leading-relaxed" style={{ color: "#94A3B8" }}>{s.text}</p>
                    </div>
                  </Reveal>
                );
              })}
            </div>
          </div>

          <div className="md:col-span-5">
            <Reveal delay={0.2}>
              <div className="relative overflow-hidden border border-white/10">
                <img
                  src="https://images.pexels.com/photos/29411310/pexels-photo-29411310.jpeg"
                  alt="Telecom field engineer"
                  className="w-full h-[320px] md:h-[420px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#060A14] via-transparent to-transparent" />
                <div className="absolute bottom-0 left-0 p-5 border-t border-l border-cyan/40">
                  <span className="mono-label text-cyan" style={{ color: "#00F0FF" }}>Field-first delivery</span>
                </div>
              </div>
            </Reveal>

            <div className="mt-px grid grid-cols-2 gap-px bg-white/5 border border-white/5 border-t-0">
              {STATS.map((s, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.08, duration: 0.6 }}
                  className="bg-[#0C1322] p-6"
                  data-testid={`stat-${i}`}
                >
                  <div className="font-display font-black text-3xl text-white tracking-tight">{s.value}</div>
                  <div className="mono-label mt-2" style={{ color: "#64748B" }}>{s.label}</div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
